import { Avatar, Button, Divider, Stack, Typography } from '@mui/material';
import { shorterDateFormat } from 'Config';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { BlueDelete, Duplicate, PlusButton } from 'Images';
import { IExpenseClaim } from 'Interfaces/expenseClaim';
import { format } from 'date-fns';

interface IListItem {
  claim: IExpenseClaim;
  onEditClaim: () => void;
  onDeleteClaim: () => void;
  onDuplicateClaim: () => void;
}

export const ListItem = ({
  claim,
  onEditClaim,
  onDeleteClaim,
  onDuplicateClaim,
}: IListItem) => {
  const dictionary = useDictionary(Dictionary, DICTIONARY.ExpenseClaim);
  const generalDictionary = useDictionary(Dictionary, DICTIONARY.General);
  const isDesktop = useIsDesktop();

  return (
    <Stack
      border='1px solid rgba(0, 0, 0, 0.08)'
      borderRadius='12px'
      boxShadow='0px 4px 24px 0px rgba(45, 112, 217, 0.16)'
      maxWidth={isDesktop ? '770px' : undefined}
      m={isDesktop ? '0 auto' : undefined}
      bgcolor={'white'}
    >
      <Stack flexDirection='row' p={'16px'} gap={'12px'}>
        <Button
          size='small'
          sx={{
            p: '0',
            height: '24px',
            width: '24px',
            minWidth: '24px',
            border: '0.5px solid rgba(244, 244, 244, 1)',
            borderRadius: '6px',
          }}
          onClick={onEditClaim}
        >
          <Avatar
            sx={{ height: '32px', width: '32px' }}
            src={PlusButton}
            alt={dictionary.minimizeForm}
          />
        </Button>
        <Typography
          fontWeight={700}
          fontSize={isDesktop ? '18px' : '16px'}
          lineHeight={'24px'}
          component={'h2'}
        >
          {dictionary.cardTitle}
        </Typography>
      </Stack>
      <Divider
        sx={{ m: '0 24px', borderColor: 'primary.shade_100' }}
        flexItem
        aria-hidden='true'
      />
      <Stack
        p={isDesktop ? '24px' : '16px'}
        sx={{ borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px' }}
      >
        <Stack
          flexDirection={'row'}
          gap={'40px'}
          sx={
            isDesktop
              ? undefined
              : { flexWrap: 'wrap', rowGap: '16px', mb: '24px' }
          }
        >
          <Stack width={'100px'} gap={'8px'}>
            <Typography variant={isDesktop ? 'body5' : 'body6'}>
              {dictionary.tripDate}
            </Typography>
            <Typography variant={isDesktop ? 'body1' : 'body2'}>
              {format(claim.travelDate, shorterDateFormat)}
            </Typography>
          </Stack>
          {claim.publicTransportPrice && (
            <Stack width={'100px'} gap={'8px'}>
              <Typography variant={isDesktop ? 'body5' : 'body6'}>
                {dictionary.tripCost}
              </Typography>
              <Typography variant={isDesktop ? 'body1' : 'body2'}>
                {`${claim.publicTransportPrice} ₪`}
              </Typography>
            </Stack>
          )}
          <Stack width={'100px'} gap={'8px'}>
            <Typography variant={isDesktop ? 'body5' : 'body6'}>
              {dictionary.origin}
            </Typography>
            <Typography variant={isDesktop ? 'body1' : 'body2'}>
              {claim.originPlace}
            </Typography>
          </Stack>
          <Stack width={'100px'} gap={'8px'}>
            <Typography variant={isDesktop ? 'body5' : 'body6'}>
              {dictionary.destination}
            </Typography>
            <Typography variant={isDesktop ? 'body1' : 'body2'}>
              {claim.destinationPlace}
            </Typography>
          </Stack>
        </Stack>
        <Stack
          minHeight={'100%'}
          justifyContent={'space-between'}
          alignItems={'flex-end'}
          overflow={'hidden'}
        >
          <Stack
            gap={'12px '}
            flexDirection={'row'}
            justifyContent={'flex-end'}
          >
            <Button
              size='small'
              sx={{
                p: '0',
                height: '20px',
                border: '0.5px solid rgba(244, 244, 244, 1)',
                borderRadius: '6px',
                gap: '6px',
              }}
              onClick={onDeleteClaim}
            >
              <Avatar
                sx={{ width: '20px', height: '20px' }}
                src={BlueDelete}
                alt={''}
              />
              <Typography variant='body5'>
                {generalDictionary.delete}
              </Typography>
            </Button>
            <Divider flexItem orientation='vertical' aria-hidden='true' />
            <Button
              size='small'
              sx={{
                p: '0',
                height: '20px',
                border: '0.5px solid rgba(244, 244, 244, 1)',
                borderRadius: '6px',
                gap: '6px',
              }}
              onClick={onDuplicateClaim}
            >
              <Avatar
                sx={{ width: '20px', height: '20px' }}
                src={Duplicate}
                alt={''}
              />
              <Typography variant='body5'>
                {generalDictionary.duplicate}
              </Typography>
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
